/**
 * Lo que la sección Usados le agrega a cada fila de la lista común.
 *
 * Los usados son el único caso del catálogo donde una fila es UNA cosa física
 * y no un renglón repetible: dos "15 Pro 128" son dos equipos distintos, con
 * distinto color, distinta batería, distinto IMEI y distinto costo de toma.
 * Eso no cambia la lista —columnas de plata, filtros y densidad son los mismos
 * que en Accesorios— pero sí cambia qué hay que poder ver sin abrir la ficha.
 *
 * CADA DATO EN UN SOLO LUGAR
 * --------------------------
 * Hubo un momento en que la capacidad, el color y la batería estaban tres
 * veces en la misma fila: adentro del nombre, otra vez en una segunda línea
 * gris, y la batería además en el chip. Tres copias del mismo dato ocupan
 * tres anchos y no agregan nada; si además una se desactualiza, mienten.
 *
 * Ahora cada cosa está una vez y en el lugar que le corresponde:
 *
 *   · El NOMBRE dice modelo y capacidad, que es lo que no cambia.
 *   · El COLOR y la BATERÍA tienen su columna: son lo que se compara entre
 *     filas cuando hay tres iPhone 14 y hay que elegir cuál se ofrece.
 *   · El IMEI, la caja, la garantía y las observaciones van al DETALLE, que se
 *     abre apretando la fila: se consultan de a un equipo, no recorriendo.
 *   · Los CHIPS dicen QUÉ ES esa fila: está en el taller, o es un precio de
 *     referencia y no un equipo. Lo que le falta va aparte, en el contador.
 *   · «Ya lo tengo» es la acción de los a pedido y no existe en otra sección.
 */
import { PackageCheck, TriangleAlert } from "lucide-react";

import type { ExtrasDeFila } from "@/components/catalogo/ListaProductos";
import { ETIQUETA_MARCA, marcasDe } from "@/components/catalogo/ListaProductos";
import { Badge } from "@/components/ui/badge";
import { UnidadesDelModelo } from "@/components/catalogo/UnidadesDelModelo";
import { Codigo } from "@/components/ui/codigo";
import { IconButton } from "@/components/ui/icon-button";
import { stockConUnidades } from "@/components/iphones/logica";
import { aNumero, type Producto, type UsadoEnTaller } from "@/lib/tipos";

import { MuestraColor } from "@/components/catalogo/MuestraColor";

import { detalleUsado, esAPedido, esReferencia, pendientesUsado } from "./logica";

export interface ExtrasUsadosProps {
  /** Cuáles están hoy en el técnico, por id de producto. */
  enTaller?: ReadonlyMap<number, UsadoEnTaller>;
  /** «Ya lo tengo»: el equipo a pedido llegó y pasa a ser stock del local. */
  onRecibir?: (p: Producto) => void;
  /** Los equipos de cada modelo, por id del precio de referencia. */
  unidades?: ReadonlyMap<number, Producto[]>;
  onEditar?: (p: Producto) => void;
}

export function extrasDeUsados({
  enTaller,
  onRecibir,
  unidades,
  onEditar,
}: ExtrasUsadosProps): ExtrasDeFila {
  return {
    columnas: [
      { titulo: "Color", celda: (p) => <MuestraColor color={p.color} /> },
      {
        titulo: "Batería",
        alineacion: "derecha",
        celda: (p) =>
          p.bateria_pct === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            `${p.bateria_pct}%`
          ),
      },
    ],

    detalle: (p) => {
      /**
       * Una referencia no es un equipo y no tiene IMEI ni caja que contar: lo
       * que se abre sobre ella es el INVENTARIO de ese modelo, que es la
       * pregunta que sigue a «¿cuánto sale un 13 usado?».
       */
      if (esReferencia(p)) {
        if (!onEditar) return null;
        return (
          <UnidadesDelModelo
            unidades={unidades?.get(p.id) ?? []}
            onEditar={onEditar}
            vacio="No hay ninguno de este modelo en el local. El precio de arriba es la referencia para tomar y vender."
          />
        );
      }

      const lineas = detalleUsado(p);
      if (lineas.length === 0) return null;
      // Las marcas que la fila no muestra: acá abajo siguen estando.
      const bajadas = marcasDe(p).filter((m) => m === "precioManual");
      const faltan = pendientesUsado(p);
      return (
        <div className="space-y-2">
          {/* Lo que falta, arriba y en palabras: el contador de la fila dice
              cuantos son, esto dice cuales y es el motivo de haber abierto. */}
          {faltan.length > 0 ? (
            <p className="flex items-center gap-1.5 text-xs font-medium text-warning">
              <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0" />
              {faltan.join(" · ")}
            </p>
          ) : null}
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            {lineas.map((l) => (
              <div key={l.etiqueta} className="flex gap-2">
                <dt className="shrink-0 text-muted-foreground">{l.etiqueta}</dt>
                <dd className="min-w-0 break-words">
                  {l.etiqueta === "IMEI" && p.imei ? <Codigo>{l.valor}</Codigo> : l.valor}
                </dd>
              </div>
            ))}
          </dl>
          {bajadas.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              {bajadas.map((m) => ETIQUETA_MARCA[m]).join(" · ")}
            </p>
          ) : null}
        </div>
      );
    },

    /**
     * De las marcas comunes, en la fila quedan las que cambian QUE SE PUEDE
     * HACER con el equipo: un «a pedido» no se puede entregar hoy y uno dado
     * de baja no se vende. «Precio a mano» y «Publicado» bajan al detalle:
     * estaban en casi todas las filas —no distinguían nada— y se llevaban el
     * ancho del nombre, que es el dato que sí hay que leer.
     */
    marcas: (p) => marcasDe(p).filter((m) => m === "aPedido" || m === "dadoDeBaja"),

    /**
     * Lo que le falta a la ficha para estar completa. No es un estado del
     * equipo: es trabajo pendiente, y por eso va al contador y no a un chip.
     *
     * Un vendido no entra: el IMEI que no se anotó ya no se puede anotar, y
     * marcar para siempre algo que no tiene arreglo es ruido permanente.
     */
    pendientes: pendientesUsado,

    stock: (p) => (unidades ? stockConUnidades(p, unidades) : aNumero(p.stock_actual)),

    /**
     * No hay chip «Vendido»: un equipo que ya se fue no llega hasta acá. La
     * pantalla contesta «qué vendo y a cuánto», y la ficha del vendido sigue
     * viva para la venta, el remito y el historial — pero no es catálogo.
     */
    chips: (p) => {
      const enElTaller = enTaller?.get(p.id);
      return (
        <>
          {esReferencia(p) ? (
            <Badge variant="outline" title="Cuánto vale el modelo usado: no es un equipo en stock">
              Referencia
            </Badge>
          ) : null}
          {enElTaller ? <Badge variant="warning">En taller · {enElTaller.numero}</Badge> : null}
        </>
      );
    },

    // Solo a un EQUIPO del proveedor. Un renglon de la lista a pedido no es un
    // telefono: recibirlo le cargaria bateria e IMEI y la lista perderia el
    // precio del modelo. El equipo que llega se carga aparte.
    accion: (p) =>
      esAPedido(p) && !esReferencia(p) && onRecibir ? (
        <IconButton
          icono={PackageCheck}
          tamano="sm"
          aria-label={`Ya tengo ${p.nombre}`}
          title="Ya lo tengo: pasarlo a stock"
          // Recibir no es desplegar la fila: el clic se queda en el boton.
          onClick={(e) => {
            e.stopPropagation();
            onRecibir(p);
          }}
        />
      ) : null,
  };
}
